import { Guid } from "client_legacy/ndaw/guid";
import {
	ReactElement,
	useMemo,
	useState
} from "react";
import { excludeGroupButPromoteChildren, HubItemGroupNodeFormModel } from "./common";
import { localize } from "client_legacy/ndaw/culture";
import { isNil } from "client/Misc/utils";
import {
	ActionListItem,
	DropOverActionList
} from "client/Components";
import { GroupSelectionMenu } from "./GroupSelectionMenu";
import { never } from "client_legacy/ndaw/errors";
import { OpenItemMenuState, useItemDropOverMenuProps } from "client/Hooks";

export type CategoryItemMenuProps = {
	target: HTMLElement;
	onClose: () => void;
	menuState: OpenItemMenuState;
	containingGroupId?: Guid;
	groupsHierarchy: readonly HubItemGroupNodeFormModel[];
	onEdit(): void;
	onDelete(): void;
	onMoveToGroup(targetGroupId: Guid): void;
	onMoveToTop?(): void;
	trackingId: string;
};

export const CategoryItemMenu = (props: CategoryItemMenuProps): ReactElement => {
	const {
		target,
		onClose,
		menuState,
		containingGroupId,
		groupsHierarchy,
		onEdit,
		onMoveToGroup,
		onMoveToTop,
		onDelete,
		trackingId
	} = props;

	const [view, setView] = useState<View>("main");

	const {
		contentAlignment,
		targetAlignment,
		closeOnMouseDown
	} = useItemDropOverMenuProps(
		menuState,
		{
			moreMenuTargetAlignment: "br",
			moreMenuContentAlignment: "tr"
		}
	);

	const doesAnyGroupExistToMoveIn = useMemo(
		(): boolean => isNil(containingGroupId)
			? groupsHierarchy.length > 0
			: excludeGroupButPromoteChildren(containingGroupId, groupsHierarchy)
				.length > 0,
		[containingGroupId, groupsHierarchy]
	);

	const moveToGroupTitle: string = localize(
		isNil(containingGroupId)
			? "_MoveToGroup"
			: "_MoveToAnotherGroup"
	);

	const renderItems = () => {
		switch(view) {
			case "main":
				return (
					<>
						{onMoveToTop && (
							<ActionListItem
								onClick={onMoveToTop}
								text={localize("_MoveToTopLevel")}
								trackingId="move-to-top-level"
								icon="arrow-up-to-line"
							/>
						)}
						{doesAnyGroupExistToMoveIn && (
							<ActionListItem
								onClick={() => setView("group-selection")}
								text={moveToGroupTitle}
								trackingId="move-to-another-group"
								icon="arrow-right-to-line"
								rightIcon="chevron-right"
								shouldNotAutoCloseContainingDropOver
							/>
						)}
						<ActionListItem
							onClick={onEdit}
							text={localize("_Edit")}
							trackingId="edit-category"
							icon="pen"
						/>
						<ActionListItem
							onClick={onDelete}
							text={localize("_Delete")}
							trackingId="delete-category"
							icon="trash-can"
						/>
					</>
				);
			case "group-selection":
				return (
					<GroupSelectionMenu
						containingGroupId={containingGroupId}
						groupsHierarchy={groupsHierarchy}
						moveToGroupTitle={moveToGroupTitle}
						onMoveToGroup={onMoveToGroup}
						onBack={() => setView("main")}
					/>
				);
			default:
				never(view);
		}
	};

	return (
		<DropOverActionList
			target={target}
			onClose={() => onClose()}
			menuTitle={localize("_Add")}
			targetAlignment={targetAlignment}
			contentAlignment={contentAlignment}
			closeOnMouseDown={closeOnMouseDown}
			trackingId={trackingId}
		>
			{renderItems()}
		</DropOverActionList>
	);
};
CategoryItemMenu.displayName = "CategoryItemMenu";

type View = "main" | "group-selection";